import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { combineLatest, Subscription } from 'rxjs';
import { XdsCoachmarkComponent, XdsCoachmarkFooterDirective } from 'src/app/shared/ui/coachmark/coachmark.component';
import { XdsButtonComponent } from 'src/app/shared/ui/button/button.component';
import { TourCoordinator } from '../../services/tour-coordinator.service';
import { FeatureFlagsService } from '../../services/feature-flags.service';
import { DialogAwarenessService } from '../../services/dialog-awareness.service';
import { OfferedTour } from '../../models/offered-tour.model';

/**
 * Dezenter Nudge-Banner unten rechts, links neben dem Help-Hub-Launcher.
 * Zeigt angebotene Touren (TourCoordinator.offeredTours$) und ermoeglicht
 * Ja/Spaeter/Schliessen. Bewusst unten platziert, damit er die Toolbar
 * (Freigeben/Auto-Vorschlag/…) nicht verdeckt und wie eine Erinnerung am
 * Hilfe-Button sitzt.
 *
 * UX-Logik:
 * - Ja  → TourCoordinator.acceptOfferedTour → startTour (force)
 * - Spaeter → snoozeOfferedTour(24h) (Tour wird in 24h wieder angeboten)
 * - X → dismissOfferedTour (kurzer Offer-Cooldown, kein Tour-Dismiss)
 *
 * Der Banner ist bewusst NICHT blockierend: User kann die App normal
 * bedienen, wenn er das Angebot ignoriert.
 */
@Component({
  selector: 'app-onboarding-nudge',
  standalone: true,
  imports: [CommonModule, TranslateModule, XdsCoachmarkComponent, XdsCoachmarkFooterDirective, XdsButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <xds-coachmark
      *ngIf="currentOffer"
      class="onboarding-nudge"
      size="sm"
      [compact]="true"
      icon="fa-light fa-sparkles"
      [titleKey]="currentOffer.title"
      [bodyKey]="currentOffer.description"
      (closed)="dismiss()">
      <div xdsCoachmarkFooter>
        <xds-button variant="ghost" size="sm" (clicked)="snooze()">
          {{ 'ONBOARDING_NUDGE_LATER' | translate }}
        </xds-button>
        <xds-button variant="primary" size="sm" (clicked)="accept()">
          {{ 'ONBOARDING_NUDGE_ACCEPT' | translate }}
        </xds-button>
      </div>
    </xds-coachmark>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      /* Positionierung bleibt App-Logik (kein DS-Thema): Sprechblase OBERHALB
         des Help-Hub-Launchers, rechtsbuendig mit ihm. Der Launcher ist 56px
         bei right/bottom 24px (+ Safe-Area). bottom 92px = 24 + 56 + 12px
         Abstand → die Karte sitzt als Erinnerung UEBER dem Button. Der
         Klassenname .onboarding-nudge bleibt erhalten — styles.css hebt
         ihn per globaler Regel an, wenn die Planer-Publish-Bar sichtbar ist. */
      .onboarding-nudge {
        position: fixed;
        right: calc(24px + env(safe-area-inset-right, 0px));
        bottom: calc(92px + env(safe-area-inset-bottom, 0px));
        z-index: 1250;
        width: 320px;
        max-width: calc(100vw - 48px);
        animation: nudge-in 0.25s cubic-bezier(0.2, 0.9, 0.3, 1.2);
      }
      /* Mobile: der Launcher steht hoeher (bottom:80px, 56px hoch). Die Karte
         legt sich als volle Breite direkt UEBER den Launcher, statt seitlich
         (kein Platz fuer 320px neben dem Button auf schmalen Screens). */
      @media (max-width: 600px) {
        .onboarding-nudge {
          right: calc(16px + env(safe-area-inset-right, 0px));
          left: calc(16px + env(safe-area-inset-left, 0px));
          bottom: calc(148px + env(safe-area-inset-bottom, 0px));
          width: auto;
          max-width: none;
        }
      }
      @keyframes nudge-in {
        from {
          opacity: 0;
          transform: translateY(8px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    `,
  ],
})
export class OnboardingNudgeComponent implements OnInit, AfterViewChecked, OnDestroy {
  currentOffer: OfferedTour | null = null;

  private sub?: Subscription;
  private killSub?: Subscription;
  private killed = false;
  /** Zuletzt gemeldete Höhe — verhindert Schreiben in jedem Change-Detection-Lauf. */
  private gemeldeteHoehe = -1;

  constructor(
    private readonly coordinator: TourCoordinator,
    private readonly cdr: ChangeDetectorRef,
    private readonly featureFlags: FeatureFlagsService,
    private readonly dialogAwareness: DialogAwarenessService,
    private readonly host: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
  ) {}

  ngOnInit(): void {
    // Banner gegen aktive Tour UND offene Dialoge gaten:
    // - Solange irgendeine Tour laeuft, wird der Nudge ausgeblendet, damit
    //   Popover und Banner sich nicht ueberlappen.
    // - Solange ein modaler Dialog/Overlay (mat-dialog) offen ist, bleibt
    //   der Nudge ebenfalls verborgen — sonst legt sich das Banner oben
    //   rechts ueber den Dialog (UX-Audit findings/07 #1: „Erste Abteilung"-
    //   Coachmark ueberlagerte den Schichtvorlagen-Wizard).
    // Das Offer selbst bleibt im Subject erhalten — endet die Tour bzw.
    // schliesst der Dialog, taucht der Nudge automatisch wieder auf, falls
    // das Angebot noch relevant ist.
    this.sub = combineLatest([
      this.coordinator.offeredTours$,
      this.coordinator.activeTour$,
      this.dialogAwareness.hasOpenDialog$,
    ]).subscribe(([offers, activeTour, hasOpenDialog]) => {
      const blocked = this.killed || activeTour !== null || hasOpenDialog;
      this.currentOffer = blocked ? null : (offers[0] ?? null);
      this.cdr.markForCheck();
    });
    // Zweite Absicherung: wenn der Kill-Switch zur Laufzeit aktiviert
    // wird, verschwindet auch ein bereits angebotener Nudge sofort. Die
    // primaere Gate liegt im TourCoordinator (offerTour prueft den
    // Switch), aber Settings-Changes koennen waehrend einer bestehenden
    // Offer passieren.
    this.killSub = this.featureFlags.onboardingGloballyDisabled$.subscribe(
      (disabled) => {
        this.killed = disabled;
        if (disabled) this.currentOffer = null;
        this.cdr.markForCheck();
      },
    );
  }

  /**
   * Go-live-Befund C-07: Der Nudge liegt fest positioniert über dem Seiteninhalt
   * (`bottom: 92px`, `z-index: 1250`) und verdeckte dort die rechten Spalten der unteren
   * Tabellenzeilen — samt Zeilenmenü, also **Bedienelemente**. Scrollen half nicht: Der
   * Inhalt endete unter dem Banner.
   *
   * Statt den Nudge zu verschieben (er sitzt bewusst über dem Hilfe-Knopf), meldet er
   * jetzt seine Anwesenheit und seine **gemessene Höhe** an den Seitenrahmen. Die globale
   * Regel in `styles.css` gibt dem scrollenden Inhalt genau so viel Platz — nicht mehr.
   * Dasselbe Muster wie `body.planner-publish-bar-visible` (Body-Klasse + `Renderer2`).
   *
   * Die Höhe wird nach jedem Prüflauf abgeglichen, weil sie am Text des Angebots hängt;
   * geschrieben wird nur bei echter Änderung, sonst wäre das ein Schreibvorgang pro
   * Change-Detection-Zyklus.
   */
  ngAfterViewChecked(): void {
    const karte = this.host.nativeElement.querySelector<HTMLElement>('.onboarding-nudge');
    const hoehe = karte ? Math.round(karte.getBoundingClientRect().height) : 0;
    if (hoehe === this.gemeldeteHoehe) return;
    this.gemeldeteHoehe = hoehe;
    if (hoehe > 0) {
      this.renderer.setStyle(
        document.body,
        '--onboarding-nudge-space',
        `${hoehe}px`,
        2 /* RendererStyleFlags2.DashCase — nötig für Custom Properties */,
      );
      this.renderer.addClass(document.body, 'onboarding-nudge-visible');
    } else {
      this.freigeben();
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.killSub?.unsubscribe();
    // Ohne das bliebe der Platz reserviert, nachdem der Nudge verschwunden ist.
    this.freigeben();
  }

  private freigeben(): void {
    this.renderer.removeClass(document.body, 'onboarding-nudge-visible');
    this.renderer.removeStyle(document.body, '--onboarding-nudge-space', 2);
  }

  async accept(): Promise<void> {
    if (!this.currentOffer) return;
    await this.coordinator.acceptOfferedTour(this.currentOffer.tourId);
  }

  async snooze(): Promise<void> {
    if (!this.currentOffer) return;
    await this.coordinator.snoozeOfferedTour(this.currentOffer.tourId, 24);
  }

  async dismiss(): Promise<void> {
    if (!this.currentOffer) return;
    await this.coordinator.dismissOfferedTour(this.currentOffer.tourId);
  }
}
